import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CardSystemEditor from "../elementEditors/CardSystemEditor";
import { getSegmentDefinition, SEGMENT_TYPES } from "../segmentRegistry";
import { getLandingPreviewDevice } from "../previewDevices";
import { resolveSiteExperienceDefaults } from "../../landingEditor/siteExperienceDefaults";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  SegmentedChoiceSetting,
  PageEditorShell,
  PagePreviewTarget,
  PageHiddenTarget,
  PageEditorContextIntro,
  PageEditorBackButton,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

const PREVIEW_PRODUCT_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="960" height="640" viewBox="0 0 960 640">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#e9ecf4"/>
        <stop offset="1" stop-color="#cfd6e6"/>
      </linearGradient>
    </defs>
    <rect width="960" height="640" rx="36" fill="url(#bg)"/>
    <circle cx="760" cy="130" r="92" fill="#ffffff" opacity=".65"/>
    <rect x="285" y="118" width="390" height="404" rx="54" fill="#ffffff"/>
    <rect x="352" y="172" width="256" height="214" rx="26" fill="#6a5ae0" opacity=".88"/>
    <rect x="374" y="414" width="212" height="34" rx="17" fill="#19242f" opacity=".78"/>
    <rect x="414" y="468" width="132" height="18" rx="9" fill="#19242f" opacity=".34"/>
  </svg>
`)}`;

function normalizedCardImageHeight(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 210;
  return Math.min(420, Math.max(90, parsed));
}

function previewCardImagePosition(position) {
  if (position === "left") return "left center";
  if (position === "right") return "right center";
  return "center top";
}

function pageValue(form) {
  return form?.catalog?.productsPage || {};
}

function cardValue(form) {
  return form?.catalog?.cards || {};
}

function headerBackground(page, theme) {
  if (page.headerBackgroundColor) return page.headerBackgroundColor;
  return `radial-gradient(circle at 15% 20%, ${theme?.primaryColor || "#E44E31"} 0%, transparent 34%), linear-gradient(125deg, ${theme?.secondaryColor || "#494A98"}, #17182d)`;
}

function numericInRange(value, fallback, min, max) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

function colorWithOpacity(color, opacity, fallback = "#FFFFFF") {
  const candidate = String(color || fallback || "#FFFFFF").trim() || fallback;
  const alpha = numericInRange(opacity, 1, 0, 1);
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  return `color-mix(in srgb, ${candidate} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function previewCardShadow(cards) {
  const configured = ["shadowOpacity", "shadowX", "shadowY", "shadowBlur", "shadowSpread"]
    .some((key) => cards?.[key] !== undefined && cards?.[key] !== null && cards?.[key] !== "");
  if (!configured) return cards?.shadowColor ? `0 8px 24px ${cards.shadowColor}` : "0 10px 26px rgba(25,36,47,.10)";
  const opacity = numericInRange(cards?.shadowOpacity, 1, 0, 1);
  if (opacity <= 0) return "none";
  const x = numericInRange(cards?.shadowX, 0, -100, 100);
  const y = numericInRange(cards?.shadowY, 8, -100, 100);
  const blur = numericInRange(cards?.shadowBlur, 24, 0, 160);
  const spread = numericInRange(cards?.shadowSpread, 0, -80, 80);
  return `${x}px ${y}px ${blur}px ${spread}px ${colorWithOpacity(cards?.shadowColor || "#000000", opacity, "#000000")}`;
}

const TARGETS = Object.freeze({
  page: { label: "Página", hint: "Fondo, superficies y estados" },
  eyebrow: { label: "Texto superior", hint: "Copy del encabezado" },
  title: { label: "Título", hint: "Título general y variantes" },
  subtitle: { label: "Subtítulo", hint: "Descripción del catálogo" },
  results: { label: "Resultados", hint: "Singular y plural" },
  search: { label: "Buscador", hint: "Visibilidad y placeholder" },
  filters: { label: "Filtros", hint: "Visibilidad y etiquetas" },
  sort: { label: "Orden", hint: "Orden predeterminado" },
  pagination: { label: "Paginación", hint: "Modo y cantidad por página" },
  cards: { label: "Cards del catálogo", hint: "Sistema compartido" },
});

function targetMeta(targetId) {
  return TARGETS[targetId] || TARGETS.page;
}

const ProductsPreviewEditingContext = React.createContext(true);

function ProductsPreviewTarget({ targetId, activeTarget, onSelect, label, children, sx = {} }) {
  const editable = React.useContext(ProductsPreviewEditingContext);
  if (!editable) return <Box sx={sx}>{children}</Box>;
  return (
    <PagePreviewTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={label || targetMeta(targetId).label}
      sx={sx}
    >
      {children}
    </PagePreviewTarget>
  );
}

function ProductsHiddenTarget(props) {
  const editable = React.useContext(ProductsPreviewEditingContext);
  if (!editable) return null;
  return <PageHiddenTarget {...props} />;
}

function sortPreviewLabel(page) {
  const by = page.sortBy || "createdAt";
  const dir = page.sortDir || "desc";
  const byLabel = by === "price" ? "Precio" : by === "name" ? "Nombre" : "Más recientes";
  return `${byLabel} · ${dir === "asc" ? "Ascendente" : "Descendente"}`;
}

function productsPreviewTitle(page, navigation, catalogContext) {
  if (page.title) return page.title;
  if (catalogContext === "product") return page.productsTitle || "Productos";
  if (catalogContext === "service") return page.servicesTitle || "Servicios";
  if (catalogContext === "hybrid") return page.hybridTitle || "Productos + servicio";
  return navigation?.productsLabel || "Productos y servicios";
}

export function ProductsPagePreview({ form, activeTarget, onSelectTarget, catalogContext, previewState, device, editable = true }) {
  const page = pageValue(form);
  const cards = cardValue(form);
  const theme = form?.theme || {};
  const previewDevice = getLandingPreviewDevice(device);
  const isDesktop = previewDevice.id === "desktop";
  const isMobile = previewDevice.id === "mobile";
  const filtersVisible = page.showFilters !== false;
  const compactFilters = filtersVisible && !isDesktop;
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const headerText = page.headerTextColor || "#FFFFFF";
  const cardBackground = colorWithOpacity(cards.backgroundColor || surface, cards.backgroundOpacity ?? 1, surface);
  const cardRadius = numericInRange(cards.radius, 16, 0, 48);
  const cardGap = numericInRange(cards.gap, 18, 0, 64);
  const cardShadow = previewCardShadow(cards);
  const cardBackgroundImage = String(cards.backgroundImageUrl || "").trim();
  const cardBackgroundImageOpacity = numericInRange(cards.backgroundImageOpacity, 0.28, 0, 1);
  const cardBackgroundImageFit = cards.backgroundImageFit === "contain" ? "contain" : "cover";
  const cardBackgroundImagePosition = ["center", "top", "bottom", "left", "right"].includes(cards.backgroundImagePosition)
    ? cards.backgroundImagePosition
    : "center";
  const cardImagePosition = ["top", "left", "right"].includes(cards.imagePosition) ? cards.imagePosition : "top";
  const cardImageSide = cardImagePosition === "left" || cardImagePosition === "right";
  const showCardImage = cards.showImage !== false;
  const cardImageHorizontal = showCardImage && cardImageSide && isDesktop;
  const cardImageHeight = normalizedCardImageHeight(cards.imageHeight);
  const collapsedImageHeight = Math.max(120, Math.round(cardImageHeight * 0.76));
  const previewImageHeight = cardImageSide
    ? (isDesktop ? cardImageHeight : collapsedImageHeight)
    : (isMobile ? collapsedImageHeight : cardImageHeight);
  const cardImageFit = cards.imageFit === "contain" ? "contain" : "cover";
  const cardImageRight = cardImagePosition === "right";
  const previewResultsText = previewState === "loading"
    ? (page.loadingText ?? "Cargando catálogo…")
    : previewState === "empty"
      ? `0 ${page.resultPlural ?? "resultados"}`
      : `12 ${page.resultPlural || "resultados"}`;
  const emptyTitle = page.emptyTitle ?? "No encontramos resultados";
  const emptyText = page.emptyText ?? "Probá con otra búsqueda o quitá los filtros activos.";
  const emptyResetLabel = page.emptyResetLabel ?? "Ver todo el catálogo";

  return (
    <ProductsPreviewEditingContext.Provider value={editable}>
    <Box
      sx={{
        minHeight: 680,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: background,
        color: textColor,
        position: "relative",
      }}
      onClick={editable ? () => onSelectTarget?.("page") : undefined}
    >
      {editable ? (
        <Button
          size="small"
          variant={activeTarget === "page" ? "contained" : "outlined"}
          startIcon={<TuneRoundedIcon />}
          onClick={(event) => {
            event.stopPropagation();
            onSelectTarget?.("page");
          }}
          sx={{ position: "absolute", zIndex: 8, top: 10, right: 10, bgcolor: activeTarget === "page" ? undefined : "rgba(255,255,255,.94)" }}
        >
          Página
        </Button>
      ) : null}

      <Box sx={{ px: isDesktop ? 5 : 2, pt: isDesktop ? 9 : 7, pb: 5, color: headerText, background: headerBackground(page, theme) }}>
        <Stack spacing={1.1} sx={{ minWidth: 0 }}>
          <ProductsPreviewTarget targetId="eyebrow" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" variant="overline" sx={{ color: "inherit", opacity: page.eyebrow ? 0.72 : 0.48, fontStyle: page.eyebrow ? "normal" : "italic" }}>
              {page.eyebrow || "Texto superior"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="title" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" variant="h3" sx={{ color: "inherit", fontWeight: 900, overflowWrap: "anywhere" }}>
              {productsPreviewTitle(page, form?.navigation, catalogContext)}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="subtitle" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: page.subtitle ? 0.82 : 0.48, fontStyle: page.subtitle ? "normal" : "italic", overflowWrap: "anywhere" }}>
              {page.subtitle || "Subtítulo opcional"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="results" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: 0.72 }}>
              {previewResultsText}
            </Typography>
          </ProductsPreviewTarget>
        </Stack>
      </Box>

      <Box sx={{ px: isDesktop ? 4 : 1.5, pb: 5, minWidth: 0 }}>
        {(page.showSearch !== false || compactFilters) ? (
          <Box
            sx={{
              mt: -2.5,
              p: 1.4,
              bgcolor: surface,
              borderRadius: 2.5,
              boxShadow: "0 14px 36px rgba(25,36,47,.14)",
              display: "flex",
              gap: 1.25,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            {page.showSearch !== false ? (
              <ProductsPreviewTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  data-preview-passive="true"
                  fullWidth
                  size="small"
                  disabled
                  placeholder={page.searchPlaceholder || "Buscar por nombre, servicio, código, categoría o subcategoría"}
                  InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: mutedText }} /> }}
                />
              </ProductsPreviewTarget>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}

            {compactFilters ? (
              <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: "0 0 auto" }}>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="outlined"
                  startIcon={<FilterListRoundedIcon />}
                  sx={{ minHeight: 40, minWidth: isMobile ? 42 : 118, px: isMobile ? 1 : 1.5, pointerEvents: "none" }}
                >
                  {isMobile ? null : (page.filtersLabel || "Filtros")}
                </Button>
              </ProductsPreviewTarget>
            ) : null}
          </Box>
        ) : null}

        {page.showSearch === false ? (
          <Box sx={{ mt: compactFilters ? 1 : -2.5 }}>
            <ProductsHiddenTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} label="Buscador" />
          </Box>
        ) : null}

        {editable ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 2.4, mb: -1, minWidth: 0 }}>
            <ProductsPreviewTarget targetId="sort" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
              <Chip data-preview-passive="true" size="small" icon={<TuneRoundedIcon />} label={`Orden · ${sortPreviewLabel(page)}`} />
            </ProductsPreviewTarget>
            <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget}>
              <Chip data-preview-passive="true" size="small" variant="outlined" label={`${Number(page.pageSize) || 24} por página`} />
            </ProductsPreviewTarget>
          </Stack>
        ) : null}

        {!filtersVisible ? (
          <Box sx={{ mt: 3 }}>
            <ProductsHiddenTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} label="Filtros" />
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: filtersVisible && isDesktop ? "220px minmax(0,1fr)" : "minmax(0,1fr)",
            gap: 2.5,
            minWidth: 0,
          }}
        >
          {filtersVisible && isDesktop ? (
            <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ alignSelf: "start", minWidth: 0 }}>
              <Box data-preview-passive="true" sx={{ bgcolor: surface, borderRadius: 2.5, p: 2, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <FilterListRoundedIcon fontSize="small" />
                  <Typography fontWeight={900}>{page.filtersLabel || "Filtros"}</Typography>
                </Stack>
                <Stack spacing={1.2}>
                  {page.showTypeFilter !== false ? <Chip label={page.typeFilterLabel || "Tipo"} variant="outlined" /> : null}
                  {page.showCategoryFilter !== false ? <Chip label={page.categoryFilterLabel || "Categoría"} variant="outlined" /> : null}
                  {page.showSubcategoryFilter !== false ? <Chip label={page.subcategoryFilterLabel || "Subcategoría"} variant="outlined" /> : null}
                </Stack>
              </Box>
            </ProductsPreviewTarget>
          ) : null}

          <Box sx={{ minWidth: 0 }}>
            {previewState === "loading" ? (
              <Box
                role="status"
                aria-label={page.loadingText ?? "Cargando catálogo…"}
                sx={{ minHeight: 360, display: "grid", placeItems: "center", minWidth: 0 }}
              >
                <CircularProgress />
              </Box>
            ) : previewState === "empty" ? (
              <Paper
                variant="outlined"
                sx={{ p: isMobile ? 3 : 5, textAlign: "center", bgcolor: surface, color: textColor, borderRadius: 2.5, minWidth: 0 }}
              >
                <Typography variant="h4" sx={{ color: textColor }}>
                  {emptyTitle}
                </Typography>
                <Typography sx={{ mt: 1, color: mutedText }}>
                  {emptyText}
                </Typography>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="contained"
                  sx={{ mt: 3, pointerEvents: "none" }}
                >
                  {emptyResetLabel}
                </Button>
              </Paper>
            ) : (
              <>
                {editable ? (
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.2 }}>
                    <Chip size="small" variant="outlined" label="Contenido de ejemplo" />
                    <Typography variant="caption" sx={{ color: mutedText }}>
                      El diseño se aplica al catálogo real; estos productos no se guardan.
                    </Typography>
                  </Stack>
                ) : null}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${isDesktop ? 3 : 2}, minmax(0,1fr))`,
                    gap: `${cardGap}px`,
                    minWidth: 0,
                  }}
                >
                  {["Producto destacado", "Servicio principal", "Nueva colección", "Más vendido", "Producto especial", "Servicio premium"].map((name, index) => (
                    <ProductsPreviewTarget
                      key={name}
                      targetId="cards"
                      activeTarget={activeTarget}
                      onSelect={onSelectTarget}
                      label="Cards del catálogo"
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        data-preview-passive="true"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: cardImageHorizontal
                            ? (cardImageRight ? "62% minmax(0,38%)" : "38% minmax(0,62%)")
                            : "minmax(0,1fr)",
                          gridTemplateRows: cardImageHorizontal
                            ? "minmax(0,1fr)"
                            : (showCardImage ? "auto minmax(0,1fr)" : "minmax(0,1fr)"),
                          minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                          minWidth: 0,
                          overflow: "hidden",
                          borderRadius: `${cardRadius}px`,
                          bgcolor: cardBackground,
                          border: cards.borderColor ? `1px solid ${cards.borderColor}` : "1px solid rgba(25,36,47,.08)",
                          boxShadow: cardShadow,
                          position: "relative",
                          isolation: "isolate",
                          "&::before": cardBackgroundImage ? {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: "none",
                            backgroundImage: `url("${cardBackgroundImage.replace(/"/g, "%22")}")`,
                            backgroundSize: cardBackgroundImageFit,
                            backgroundPosition: cardBackgroundImagePosition,
                            backgroundRepeat: "no-repeat",
                            opacity: cardBackgroundImageOpacity,
                          } : undefined,
                          "& > *": { position: "relative", zIndex: 1 },
                        }}
                      >
                        {showCardImage ? (
                          <Box
                            sx={{
                              minWidth: 0,
                              overflow: "hidden",
                              bgcolor: cardImageFit === "contain" ? "#fff" : "rgba(25,36,47,.08)",
                              gridColumn: cardImageHorizontal ? (cardImageRight ? 2 : 1) : 1,
                              gridRow: cardImageHorizontal ? 1 : 1,
                              minHeight: cardImageHorizontal ? cardImageHeight : 0,
                            }}
                          >
                            <Box
                              component="img"
                              src={PREVIEW_PRODUCT_IMAGE}
                              alt=""
                              aria-hidden="true"
                              sx={{
                                display: "block",
                                width: "100%",
                                height: cardImageHorizontal ? "100%" : previewImageHeight,
                                minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                                objectFit: cardImageFit,
                                objectPosition: previewCardImagePosition(cardImagePosition),
                              }}
                            />
                          </Box>
                        ) : null}
                        <Box
                          sx={{
                            p: 1.5,
                            minWidth: 0,
                            gridColumn: cardImageHorizontal ? (cardImageRight ? 1 : 2) : 1,
                            gridRow: cardImageHorizontal ? 1 : (showCardImage ? 2 : 1),
                            alignSelf: "stretch",
                          }}
                        >
                          {cards.showType !== false ? (
                            <Typography variant="caption" sx={{ color: cards.typeColor || theme.primaryColor || "#F35A3A", fontWeight: 850 }}>
                              Producto
                            </Typography>
                          ) : null}
                          {cards.showTitle !== false ? (
                            <Typography sx={{ mt: 0.3, fontWeight: 900, color: cards.titleColor || textColor, overflowWrap: "anywhere" }}>
                              {name}
                            </Typography>
                          ) : null}
                          {cards.showCategory !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: cards.categoryColor || mutedText }}>
                              Categoría
                            </Typography>
                          ) : null}
                          {cards.showSubcategory === true ? (
                            <Typography variant="caption" sx={{ display: "block", color: cards.subcategoryColor || mutedText }}>
                              Subcategoría
                            </Typography>
                          ) : null}
                          {cards.showDescription !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: cards.descriptionColor || mutedText }}>
                              Descripción breve de ejemplo.
                            </Typography>
                          ) : null}
                          {cards.showDuration !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: cards.descriptionColor || mutedText }}>
                              Duración: 60 min
                            </Typography>
                          ) : null}
                          {cards.showCapabilities !== false ? (
                            <Stack direction="row" spacing={0.45} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                              <Chip size="small" label="Reserva" />
                              <Chip size="small" label="Entrega" />
                            </Stack>
                          ) : null}
                          {cards.showPrice !== false ? (
                            <Typography sx={{ mt: 0.8, fontWeight: 900, color: cards.priceColor || theme.secondaryColor || "#19242F" }}>
                              $ {index % 2 ? "25.000" : "18.500"}
                            </Typography>
                          ) : null}
                          {cards.showQuickCart !== false ? (
                            <Chip size="small" label="Compra rápida" sx={{ mt: 0.7 }} />
                          ) : null}
                          {cards.showButton !== false ? (
                            <Button
                              tabIndex={-1}
                              size="small"
                              variant="contained"
                              fullWidth
                              sx={{
                                mt: 1.2,
                                pointerEvents: "none",
                                textTransform: "none",
                                color: cards.buttonTextColor || "#FFFFFF",
                                bgcolor: cards.buttonBackgroundColor || theme.primaryColor || "#6A5AE0",
                                "&:hover": {
                                  color: cards.buttonHoverTextColor || cards.buttonTextColor || "#FFFFFF",
                                  bgcolor: cards.buttonHoverBackgroundColor || cards.buttonBackgroundColor || theme.primaryColor || "#6A5AE0",
                                },
                              }}
                            >
                              {cards.ctaLabel || "Ver detalle"}
                            </Button>
                          ) : null}
                        </Box>
                      </Box>
                    </ProductsPreviewTarget>
                  ))}
                </Box>

                <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 3, minWidth: 0 }}>
                  <Box data-preview-passive="true" sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                    {page.paginationMode === "loadMore" ? (
                      <Button tabIndex={-1} variant="outlined" sx={{ pointerEvents: "none" }}>
                        {page.loadMoreLabel || "Cargar más"}
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="center">
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Anterior</Button>
                        <Typography variant="body2" sx={{ color: mutedText }}>Página 1 de 4</Typography>
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Siguiente</Button>
                      </Stack>
                    )}
                  </Box>
                </ProductsPreviewTarget>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
    </ProductsPreviewEditingContext.Provider>
  );
}

export function ProductsPageDraftPreview({ form, device, gastronomicMenuEnabled = false, productsEnabled = true }) {
  const previewForm = resolveSiteExperienceDefaults(form, { gastronomicMenuEnabled, productsEnabled });
  return (
    <ProductsPagePreview
      form={previewForm}
      activeTarget={null}
      onSelectTarget={undefined}
      catalogContext="none"
      previewState="results"
      device={device}
      editable={false}
    />
  );
}

function PageCopyEditor({ targetId, page, set, catalogContext, onCatalogContextChange }) {
  if (targetId === "eyebrow") {
    return <TextSetting label="Texto superior" value={page.eyebrow} onChange={(value) => set("eyebrow", value)} />;
  }
  if (targetId === "subtitle") {
    return <TextSetting label="Subtítulo" value={page.subtitle} onChange={(value) => set("subtitle", value)} multiline />;
  }
  if (targetId === "results") {
    return (
      <EditorGroup title="Cómo mostramos la cantidad" description="Preview: 1 resultado / 12 resultados">
        <TextSetting label="Singular" value={page.resultSingular} onChange={(value) => set("resultSingular", value)} />
        <TextSetting label="Plural" value={page.resultPlural} onChange={(value) => set("resultPlural", value)} />
      </EditorGroup>
    );
  }
  return (
    <Stack spacing={1.5}>
      <TextSetting label="Título general" value={page.title} onChange={(value) => set("title", value)} helperText="Vacío = usar el nombre del catálogo de la navegación." />
      <EditorGroup title="Título según el tipo de catálogo" description="Se usa cuando el título general está vacío y corresponde distinguir el catálogo.">
        <TextSetting label="Productos" value={page.productsTitle} onChange={(value) => set("productsTitle", value)} />
        <TextSetting label="Servicios" value={page.servicesTitle} onChange={(value) => set("servicesTitle", value)} />
        <TextSetting label="Productos + servicio" value={page.hybridTitle} onChange={(value) => set("hybridTitle", value)} />
        <Divider />
        <SelectSetting
          label="Simular catálogo en preview"
          value={catalogContext}
          onChange={onCatalogContextChange}
          options={[
            { value: "none", label: "Sin filtro" },
            { value: "product", label: "Productos" },
            { value: "service", label: "Servicios" },
            { value: "hybrid", label: "Productos + servicio" },
          ]}
          helperText="Sólo cambia esta vista previa; el tipo real depende del filtro que use el visitante."
        />
        {page.title ? (
          <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.64)" }}>
            El título general reemplaza las variantes por tipo.
          </Typography>
        ) : null}
      </EditorGroup>
    </Stack>
  );
}

function SearchEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar buscador" checked={page.showSearch} onChange={(value) => set("showSearch", value)} />
      <TextSetting label="Texto dentro del buscador" value={page.searchPlaceholder} onChange={(value) => set("searchPlaceholder", value)} />
    </Stack>
  );
}

function FiltersEditor({ page, set }) {
  const filtersVisible = page.showFilters !== false;

  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar filtros" checked={page.showFilters} onChange={(value) => set("showFilters", value)} />
      {filtersVisible ? (
        <>
          <EditorGroup title="Qué filtros mostrar">
            <ToggleSetting label="Tipo" checked={page.showTypeFilter} onChange={(value) => set("showTypeFilter", value)} />
            <ToggleSetting label="Categoría" checked={page.showCategoryFilter} onChange={(value) => set("showCategoryFilter", value)} />
            <ToggleSetting label="Subcategoría" checked={page.showSubcategoryFilter} onChange={(value) => set("showSubcategoryFilter", value)} />
          </EditorGroup>
          <EditorGroup title="Textos de filtros">
            <TextSetting label="Título" value={page.filtersLabel} onChange={(value) => set("filtersLabel", value)} />
            <TextSetting label="Tipo" value={page.typeFilterLabel} onChange={(value) => set("typeFilterLabel", value)} />
            <TextSetting label="Categoría" value={page.categoryFilterLabel} onChange={(value) => set("categoryFilterLabel", value)} />
            <TextSetting label="Subcategoría" value={page.subcategoryFilterLabel} onChange={(value) => set("subcategoryFilterLabel", value)} />
            <Divider />
            <TextSetting label="Todos los tipos" value={page.allTypesLabel} onChange={(value) => set("allTypesLabel", value)} />
            <TextSetting label="Todas las categorías" value={page.allCategoriesLabel} onChange={(value) => set("allCategoriesLabel", value)} />
            <TextSetting label="Todas las subcategorías" value={page.allSubcategoriesLabel} onChange={(value) => set("allSubcategoriesLabel", value)} />
            <TextSetting label="Limpiar filtros" value={page.resetLabel} onChange={(value) => set("resetLabel", value)} />
            <TextSetting label="Aplicar filtros" value={page.applyFiltersLabel} onChange={(value) => set("applyFiltersLabel", value)} />
          </EditorGroup>
        </>
      ) : (
        <Paper
          variant="outlined"
          role="status"
          aria-live="polite"
          sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(35,111,231,.035)" }}
        >
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.72)" }}>
            Los filtros están ocultos en la página. Tus opciones se conservan y volverán a estar disponibles al activarlos.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}

function SortEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.64)" }}>
        Este es el orden inicial con el que se abre el catálogo.
      </Typography>
      <SelectSetting
        label="Ordenar por"
        value={page.sortBy || "createdAt"}
        onChange={(value) => set("sortBy", value)}
        options={[
          { value: "createdAt", label: "Más recientes" },
          { value: "price", label: "Precio" },
          { value: "name", label: "Nombre" },
        ]}
      />
      <SegmentedChoiceSetting
        label="Dirección"
        value={page.sortDir || "desc"}
        onChange={(value) => set("sortDir", value)}
        options={[
          { value: "desc", label: "Descendente" },
          { value: "asc", label: "Ascendente" },
        ]}
      />
    </Stack>
  );
}

function PaginationEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <SegmentedChoiceSetting
        label="Comportamiento"
        value={page.paginationMode || "all"}
        onChange={(value) => set("paginationMode", value)}
        options={[
          { value: "all", label: "Páginas" },
          { value: "loadMore", label: "Cargar más" },
        ]}
      />
      <NumberSetting
        label="Resultados por página"
        value={page.pageSize}
        min={12}
        max={60}
        onChange={(value) => set("pageSize", value)}
        helperText="Elegí entre 12 y 60 resultados por página."
      />
      {page.paginationMode === "loadMore" ? (
        <TextSetting label="Botón Cargar más" value={page.loadMoreLabel} onChange={(value) => set("loadMoreLabel", value)} />
      ) : null}
    </Stack>
  );
}

function PageSurfaceEditor({ page, theme, set, previewState, onPreviewStateChange }) {
  return (
    <Stack spacing={1.5}>
      <EditorGroup title="Apariencia de la página" description="Las cards se editan tocando una card de ejemplo.">
        <ColorSetting label="Fondo" value={page.backgroundColor} fallback={theme?.backgroundColor || "#EEF0F5"} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} fallback={theme?.surfaceColor || "#FFFFFF"} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} fallback={theme?.textColor || "#1A1A2E"} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} fallback={theme?.mutedTextColor || "#5B5D73"} onChange={(value) => set("mutedTextColor", value)} />
        <ColorSetting
          label="Fondo del encabezado"
          value={page.headerBackgroundColor}
          fallback={theme?.secondaryColor || "#494A98"}
          inheritedLabel="Heredado · marca"
          helperText={page.headerBackgroundColor ? "Color propio del encabezado." : "Sin override, la web usa el fondo de marca del catálogo."}
          onChange={(value) => set("headerBackgroundColor", value)}
        />
        <ColorSetting label="Texto del encabezado" value={page.headerTextColor} fallback="#FFFFFF" onChange={(value) => set("headerTextColor", value)} />
      </EditorGroup>
      <EditorGroup title="Mensajes y estados" description="Sólo aparecen cuando el catálogo carga o no encuentra resultados.">
        <Box>
          <Typography variant="caption" sx={{ display: "block", mb: 0.8, fontWeight: 850, color: "rgba(25,36,47,.62)" }}>
            Estado del preview
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={previewState}
            onChange={(_, value) => value && onPreviewStateChange?.(value)}
            aria-label="Estado del preview del catálogo"
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                flex: "1 1 105px",
                minWidth: 0,
                px: 1,
                textTransform: "none",
                fontWeight: 850,
              },
            }}
          >
            <ToggleButton value="results">Con resultados</ToggleButton>
            <ToggleButton value="loading">Cargando</ToggleButton>
            <ToggleButton value="empty">Sin resultados</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ display: "block", mt: 0.7, color: "rgba(25,36,47,.56)" }}>
            Sólo simula esta vista previa; no modifica la configuración del sitio.
          </Typography>
        </Box>
        <TextSetting label="Cargando" value={page.loadingText} onChange={(value) => set("loadingText", value)} />
        <TextSetting label="Título sin resultados" value={page.emptyTitle} onChange={(value) => set("emptyTitle", value)} />
        <TextSetting label="Texto sin resultados" value={page.emptyText} onChange={(value) => set("emptyText", value)} multiline />
        <TextSetting label="Botón sin resultados" value={page.emptyResetLabel} onChange={(value) => set("emptyResetLabel", value)} />
      </EditorGroup>
    </Stack>
  );
}

function catalogCardsDescriptor() {
  const definition = getSegmentDefinition(SEGMENT_TYPES.FEATURED);
  const fields = (definition?.properties || []).filter((property) => String(property?.path || "").startsWith("catalog.cards."));
  return {
    kind: "card-system",
    label: "Cards del catálogo",
    fieldPrefix: "catalog.cards.",
    fields,
  };
}

export default function ProductsPageEditor({
  form,
  device,
  gastronomicMenuEnabled = false,
  productsEnabled = true,
  saving = false,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const page = pageValue(form);
  const previewForm = resolveSiteExperienceDefaults(form, {
    gastronomicMenuEnabled,
    productsEnabled,
  });
  const [selectedTarget, setSelectedTarget] = React.useState(null);
  const [catalogContext, setCatalogContext] = React.useState("none");
  const [previewState, setPreviewState] = React.useState("results");
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productsPage.${key}`, value), [onCommit]);
  const cardsDescriptor = React.useMemo(catalogCardsDescriptor, []);
  const selectedMeta = selectedTarget ? targetMeta(selectedTarget) : { label: "Productos", hint: "Tocá un bloque del preview para editarlo." };

  let editor = null;
  if (["eyebrow", "title", "subtitle", "results"].includes(selectedTarget)) {
    editor = (
      <PageCopyEditor
        targetId={selectedTarget}
        page={page}
        set={set}
        catalogContext={catalogContext}
        onCatalogContextChange={setCatalogContext}
      />
    );
  } else if (selectedTarget === "search") {
    editor = <SearchEditor page={page} set={set} />;
  } else if (selectedTarget === "filters") {
    editor = <FiltersEditor page={page} set={set} />;
  } else if (selectedTarget === "sort") {
    editor = <SortEditor page={page} set={set} />;
  } else if (selectedTarget === "pagination") {
    editor = <PaginationEditor page={page} set={set} />;
  } else if (selectedTarget === "cards") {
    editor = (
      <CardSystemEditor
        descriptor={cardsDescriptor}
        form={form}
        disabled={saving}
        onCommit={onCommit}
        onUploadImage={onUploadImage}
        onRemoveImage={onRemoveImage}
      />
    );
  } else {
    editor = selectedTarget === "page" ? <PageSurfaceEditor page={page} theme={form?.theme || {}} set={set} previewState={previewState} onPreviewStateChange={setPreviewState} /> : null;
  }

  return (
    <PageEditorShell
      title={selectedTarget === "page" ? "Configuración general" : selectedMeta.label}
      description={selectedMeta.hint}
      surfaceLabel="Productos"
      device={device}
      saving={saving}
      contextual
      inspectorOpen={Boolean(selectedTarget)}
      onCloseInspector={() => setSelectedTarget(null)}
      preview={(
        <ProductsPagePreview
          form={previewForm}
          activeTarget={selectedTarget}
          onSelectTarget={setSelectedTarget}
          catalogContext={catalogContext}
          previewState={previewState}
          device={device}
        />
      )}
    >
      {selectedTarget && selectedTarget !== "page" ? (
        <PageEditorBackButton
          onClick={() => setSelectedTarget("page")}
          label="Configuración general"
          startIcon={<ArrowBackRoundedIcon />}
        />
      ) : selectedTarget === "page" ? (
        <PageEditorContextIntro>
          Estos ajustes afectan a la página completa. Para editar un bloque, cerrá este panel y tocá ese elemento en el preview.
        </PageEditorContextIntro>
      ) : null}
      {editor}
    </PageEditorShell>
  );
}
